import { Link, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { ChevronRight, BookOpen, ExternalLink } from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';
import { Moon, Sun } from 'lucide-react';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  useSidebar,
} from '@/components/ui/sidebar';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { Button } from '@/components/ui/button';
import { docsSections, type DocNavItem } from './docsData';
import logoLargeBlack from '../../assets/images/pliable/logo_with_text_black.png';
import logoLargeWhite from '../../assets/images/pliable/logo_with_text_white.png';

function isSlugActive(slug: string, pathname: string) {
  return pathname === `/docs/${slug}`;
}

function hasActiveChild(item: DocNavItem, pathname: string): boolean {
  if (isSlugActive(item.slug, pathname)) return true;
  return item.children?.some(c => isSlugActive(c.slug, pathname)) ?? false;
}

export default function DocsSidebar() {
  const location = useLocation();
  const { theme, toggleTheme } = useTheme();
  const { setOpenMobile } = useSidebar();

  // Close mobile sidebar on nav
  useEffect(() => {
    setOpenMobile(false);
  }, [location.pathname]);

  return (
    <Sidebar>
      <SidebarHeader className="border-b border-border px-4 py-3">
        <Link to="/" className="flex items-center gap-2">
          <img
            src={theme === 'dark' ? logoLargeWhite : logoLargeBlack}
            alt="Pliable"
            className="h-6"
          />
        </Link>
        <div className="flex items-center gap-1 mt-2">
          <BookOpen className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm font-medium text-muted-foreground">Documentation</span>
        </div>
      </SidebarHeader>

      <SidebarContent>
        {docsSections.map((section) => (
          <SidebarGroup key={section.title}>
            <SidebarGroupLabel>{section.title}</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {section.items.map((item) => (
                  <NavItem key={item.slug} item={item} pathname={location.pathname} />
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ))}
      </SidebarContent>

      <SidebarFooter className="border-t border-border p-4">
        <div className="flex items-center justify-between">
          <Link
            to="/"
            className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 transition-colors"
          >
            Go to App
            <ExternalLink className="h-3 w-3" />
          </Link>
          <Button variant="ghost" size="icon" onClick={toggleTheme} className="h-8 w-8">
            {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </Button>
        </div>
      </SidebarFooter>
    </Sidebar>
  );
}

function NavItem({ item, pathname }: { item: DocNavItem; pathname: string }) {
  const isActive = isSlugActive(item.slug, pathname);
  const isOpen = hasActiveChild(item, pathname);

  if (!item.children?.length) {
    return (
      <SidebarMenuItem>
        <SidebarMenuButton asChild isActive={isActive}>
          <Link to={`/docs/${item.slug}`}>{item.title}</Link>
        </SidebarMenuButton>
      </SidebarMenuItem>
    );
  }

  return (
    <Collapsible defaultOpen={isOpen} className="group/collapsible">
      <SidebarMenuItem>
        <CollapsibleTrigger asChild>
          <SidebarMenuButton asChild isActive={isActive}>
            <Link to={`/docs/${item.slug}`}>
              {item.title}
              <ChevronRight className="ml-auto h-4 w-4 transition-transform group-data-[state=open]/collapsible:rotate-90" />
            </Link>
          </SidebarMenuButton>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <SidebarMenuSub>
            {item.children.map((child) => (
              <SidebarMenuSubItem key={child.slug}>
                <SidebarMenuSubButton asChild isActive={isSlugActive(child.slug, pathname)}>
                  <Link to={`/docs/${child.slug}`}>{child.title}</Link>
                </SidebarMenuSubButton>
              </SidebarMenuSubItem>
            ))}
          </SidebarMenuSub>
        </CollapsibleContent>
      </SidebarMenuItem>
    </Collapsible>
  );
}
